// Encabezado de imports (ajuste de ruta y limpieza de DatePicker/InputNumber)
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Typography, Button, Space, Descriptions, Spin, message, Modal, Form, Select, Input, Popconfirm, Tag, Collapse, Table, Col, Row, Tooltip, Grid, Alert } from 'antd';
import { FileTextOutlined, FilePdfOutlined, ArrowLeftOutlined, EyeOutlined, MailOutlined, DeleteOutlined, EditOutlined, CloudUploadOutlined } from '@ant-design/icons';
import moment from 'moment';
import { userService } from '../../services/api';
import AddPaymentModal from './components/AddPaymentModal';


const { Title } = Typography;
const { Panel } = Collapse;

const InvoiceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const backPath = location?.state?.from === 'cobranza' ? '/cobranza/ppd' : '/invoices';
  const screens = Grid.useBreakpoint();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelModalVisible, setCancelModalVisible] = useState(false);
  const [motives, setMotives] = useState([]);
  const [selectedMotivo, setSelectedMotivo] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelForm] = Form.useForm();
  const isTimbrada = invoice?.status === 'timbrada';
  const allowedCodes = ['01', '02', '03', '04'];

  // Estado para pagos relacionados
  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [totalPaid, setTotalPaid] = useState(0);

  const invoiceTotal = Number(invoice?.total || 0);
  const isInvoiceFullyPaid = totalPaid >= invoiceTotal;

  const loadInvoice = async () => {
    try {
      setLoading(true);
      const resp = await userService.getInvoice(id);
      setInvoice(resp.data);
    } catch (err) {
      console.error(err);
      message.error('No se pudo cargar la factura.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInvoice();
  }, [id]);

  const getStatusColor = (s) =>
    s === 'timbrada' ? 'green' : s === 'cancelada' ? 'red' : s === 'borrador' ? 'blue' : 'default';

  // Refrescar factura tras crear/timbrar pago
  const refreshInvoice = async () => {
    try {
      const resp = await userService.getInvoice(id);
      setInvoice(resp.data);
      await loadPayments(resp?.data?.id);
    } catch (e) {
      // ... existing code ...
    }
  };

  // Cargar pagos y filtrar por la factura actual
  const loadPayments = async (invoiceIdArg = null) => {
    const targetId = invoiceIdArg ?? invoice?.id;
    if (!targetId) return;
    setPaymentsLoading(true);
    try {
      const resp = await userService.getPayments(100, 0);
      const payload = resp?.data ?? resp;
      const list = payload?.payments ?? [];
      
      const filtered = list.filter(
        (p) => Array.isArray(p?.documents) && p.documents.some((d) => d?.invoice?.id == targetId)
      );
      setPayments(filtered);

      // Calcular total pagado
      const sum = filtered.reduce((acc, p) => {
        const applied = (p.documents || [])
          .filter((d) => d?.invoice?.id == targetId)
          .reduce((s, d) => s + Number(d?.importe_pagado || 0), 0);
        return acc + applied;
      }, 0);
      setTotalPaid(sum);
    } catch (e) {
      console.error(e);
      message.error('No se pudieron cargar los pagos.');
    } finally {
      setPaymentsLoading(false);
    }
  };

  useEffect(() => {
    if (invoice?.id) {
      loadPayments(invoice.id);
    }
  }, [invoice?.id]);

  const openCancelModal = async () => {
    try {
      const check = await userService.checkInvoiceCancellation(id);
      const canCancel = check?.data?.can_cancel ?? check?.can_cancel;
      const reason = check?.data?.reason ?? check?.reason;
      if (!canCancel) {
        message.warning(reason || 'No se puede cancelar esta factura.');
        return;
      }
      const motivesResp = await userService.getCancellationMotives();
      const motivesData = motivesResp?.data ?? motivesResp;
      const filtered = Array.isArray(motivesData)
        ? motivesData.filter((m) => allowedCodes.includes(m.code))
        : [];
      setMotives(filtered);
      setSelectedMotivo(null);
      cancelForm.resetFields();
      setCancelModalVisible(true);
    } catch (e) {
      message.error('Error al verificar cancelación.');
    }
  };

  const handleConfirmCancel = async (values) => {
    try {
      const motivo = values.motivo;
      const uuidSustitucion = values.uuid_sustitucion || null;

      if (!allowedCodes.includes(motivo)) {
        message.error('Selecciona un motivo válido (01–04).');
        return;
      }

      if ((motivo === '01' || motivo === '04') && !uuidSustitucion) {
        message.error('El motivo seleccionado requiere UUID de sustitución.');
        return;
      }

      setCancelling(true);
      const resp = await userService.cancelInvoice(id, {
        motivo,
        uuid_sustitucion: uuidSustitucion,
      });
      const msg = resp?.message || 'CFDI cancelado exitosamente';
      message.success(msg);
      setCancelModalVisible(false);
      setCancelling(false);
      
      // Recargar factura para actualizar estatus
      loadInvoice();

      
    } catch (err) {
      setCancelling(false);
      const errMsg = err?.response?.data?.message || err?.message || 'Error al cancelar';
      message.error(errMsg);
    }
  };

  const [stampingPayment, setStampingPayment] = useState({});

  const handleEditPayment = (payment) => {
    // Si la factura tiene un método para abrir el modal, lo usamos
    if (invoice.openPaymentModal) {
        invoice.openPaymentModal(payment);
    }
  };

  const handleStampPayment = async (paymentId) => {
    Modal.confirm({
      title: 'Timbrar Pago',
      content: '¿Estás seguro de que deseas timbrar este pago? Una vez timbrado no se podrá editar ni eliminar.',
      okText: 'Timbrar',
      cancelText: 'Cancelar',
      onOk: async () => {
        const normalizeApiError = (err) => {
          let errMsg =
            err?.response?.data?.error ||
            err?.response?.data?.message ||
            err?.message ||
            'Error al timbrar el pago';
          
          if (typeof errMsg === 'string' && errMsg.startsWith('Error HTTP:')) {
            try {
              const jsonPart = errMsg.substring('Error HTTP:'.length, errMsg.indexOf(' - {')).trim();
              const errorObj = JSON.parse(jsonPart);
              if (errorObj && errorObj.message) {
                errMsg = errorObj.message;
              }
            } catch (e) {
              // noop
            }
          }
          
          return errMsg;
        };

        try {
          const payment = payments.find((p) => p?.id === paymentId);
          if (payment?.fecha_pago) {
            const fechaPago = moment(payment.fecha_pago);
            const now = moment();
            if (fechaPago.isAfter(now, 'day')) {
              message.error('La fecha de pago no puede ser futura. Edita el pago y corrige la fecha antes de timbrar.');
              return;
            }
          }

          setStampingPayment(prev => ({ ...prev, [paymentId]: true }));
          await userService.stampPayment(paymentId);
          message.success('Pago timbrado exitosamente');
          // Recargar datos
          const resp = await userService.getInvoice(id);
          setInvoice(resp.data || resp);
          
          setPaymentsLoading(true);
          const paymentsResp = await userService.getPayments(100, 0);
          const allPayments = paymentsResp?.data?.payments ?? [];
          const related = allPayments.filter(
            (p) => Array.isArray(p.documents) && p.documents.some((d) => d?.invoice?.id == id)
          );
          setPayments(related);
          setPaymentsLoading(false);
        } catch (error) {
          console.error('Error stamping payment:', error);
          message.error(normalizeApiError(error));
        } finally {
          setStampingPayment(prev => ({ ...prev, [paymentId]: false }));
        }
      }
    });
  };

  const handleDeletePayment = async (paymentId) => {
    try {
      await userService.deletePayment(paymentId);
      message.success('Pago eliminado');
      loadPayments(invoice.id);
    } catch (err) {
      console.error(err);
      message.error('Error al eliminar el pago');
    }
  };

  const handleCancelPayment = (paymentId) => {
    // Reutilizamos el modal de cancelación existente, pero adaptado para pagos si fuera necesario
    // O mostramos confirmación simple si la cancelación de pagos es directa
    // Por ahora, asumimos que se cancela igual que una factura normal
    setCancelModalVisible(true);
    // TODO: Adaptar handleConfirmCancel para manejar pagos o facturas según contexto
  };

  const [emailModalVisible, setEmailModalVisible] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailForm] = Form.useForm();

  const handleEmailClick = () => {
    const clientEmail = invoice?.receiver?.email || '';
    emailForm.setFieldsValue({ recipient_email: clientEmail });
    
    // Default subject and body
    const serieFolio = (invoice?.serie || '') + (invoice?.folio || '');
    const companyName = invoice?.company?.business_name || 'Mi Factura';
    
    const defaultSubject = `Factura ${serieFolio} de ${companyName}`;
    const defaultBody = `Adjunto encontrará los archivos XML y PDF de su factura.\n\nTotal: $${Number(invoice?.total || 0).toFixed(2)} ${invoice?.moneda || 'MXN'}\n\nSaludos cordiales.`;
    
    setEmailSubject(defaultSubject);
    setEmailBody(defaultBody);
    
    emailForm.setFieldsValue({
      subject: defaultSubject,
      body: defaultBody
    });
    
    setEmailModalVisible(true);
  };

  const handleSendEmail = async (values) => {
    setSendingEmail(true);
    try {
      await userService.sendInvoiceEmail(id, {
        recipients: [values.recipient_email],
        subject: values.subject,
        body: values.body
      });
      message.success('Correo enviado exitosamente');
      setEmailModalVisible(false);
    } catch (err) {
      console.error(err);
      message.error(err.response?.data?.message || 'Error al enviar el correo');
    } finally {
      setSendingEmail(false);
    }
  };

  const downloadResponseFile = (response, defaultFilename) => {
    const contentType = response.headers['content-type'] || 'application/octet-stream';
    const disposition = response.headers['content-disposition'] || '';
    const match = /filename="?([^";]+)"?/i.exec(disposition);
    const filename = match?.[1] || defaultFilename;

    let blob;
    const data = response.data;
    if (data instanceof Blob) {
      blob = data;
    } else if (data instanceof ArrayBuffer) {
      blob = new Blob([data], { type: contentType });
    } else {
      blob = new Blob([data], { type: contentType });
    }

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const [downloadingXml, setDownloadingXml] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const handleDownloadXml = async () => {
    try {
      setDownloadingXml(true);
      const resp = await userService.downloadInvoiceXml(id);
      downloadResponseFile(resp, `CFDI_${invoice?.serie || ''}_${invoice?.folio || ''}_${invoice?.uuid || ''}.xml`);
    } catch (err) {
      message.error('No se pudo descargar el XML.');
    } finally {
      setDownloadingXml(false);
    }
  };

  const downloadBase64Pdf = (base64Content, filename) => {
    if (!base64Content) {
      message.error('El PDF no se recibió correctamente.');
      return;
    }
    const cleaned = base64Content.includes(',')
      ? base64Content.split(',').pop()
      : base64Content;
    const byteChars = atob(cleaned);
    const byteNums = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
      byteNums[i] = byteChars.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNums);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadPdf = async () => {
    try {
      setDownloadingPdf(true);
      const result = await userService.generateInvoicePdf(id, { templateId: 'complitcfdiv40' });
      const base64 = result?.data?.pdf_content;
      const docPrefix = invoice?.tipo_comprobante === 'N' ? 'Nomina_' : 'CFDI_';
      const filename = `${docPrefix}${invoice?.serie || ''}_${invoice?.folio || ''}_${invoice?.uuid || ''}.pdf`;
      downloadBase64Pdf(base64, filename);
    } catch (err) {
      message.error('No se pudo generar/descargar el PDF.');
    } finally {
      setDownloadingPdf(false);
    }
  };
  const handleDownloadPaymentsPdf = async ({id}) => {
    try {
      const result = await userService.generatePaymentsPdf(id, { templateId: 'payment20' });
      const base64 = result?.data?.pdf_content;
      const docPrefix = invoice?.tipo_comprobante === 'N' ? 'Nomina_' : 'Pago_';
      const filename = `${docPrefix}_${invoice?.serie || ''}_${invoice?.folio || ''}_${invoice?.uuid || ''}.pdf`;
      downloadBase64Pdf(base64, filename);
    } catch (err) {
      message.error('No se pudo generar/descargar el PDF.');
    }
  };
  const handleDownloadPaymentsXml = async ({id}) => {
    try {
      const resp = await userService.downloadPaymentsXml(id);
      downloadResponseFile(resp, `Pagos_${invoice?.serie || ''}_${invoice?.folio || ''}_${invoice?.uuid || ''}.xml`);
    } catch (err) {
      message.error('No se pudo descargar el XML.');
    }
  };
  

  const handleDownloadAcuse = async () => {
    try {
      const resp = await userService.getCancellationStatus(id);
      const payload = resp?.data ?? resp;
      const info = payload?.data ?? payload;

      const acuse =
        info?.acuse_cancelacion ??
        info?.acuse_sat ??
        info?.acuse_completo;

      if (!acuse) {
        message.warning('No hay acuse de cancelación disponible.');
        return;
      }

      let content = acuse;
      let mime = 'text/plain';
      let filename = `Acuse_${invoice?.serie || ''}_${invoice?.folio || ''}_${invoice?.uuid || ''}.txt`;

      if (typeof acuse === 'object') {
        content = JSON.stringify(acuse, null, 2);
        mime = 'application/json';
        filename = filename.replace('.txt', '.json');
      } else if (typeof acuse === 'string') {
        const trimmed = acuse.trim();
        if (trimmed.startsWith('<')) {
          mime = 'application/xml';
          filename = filename.replace('.txt', '.xml');
          content = trimmed;
        } else if (trimmed.startsWith('data:')) {
          const [meta, base] = trimmed.split(',');
          const typeMatch = meta.match(/data:(.*?);base64/);
          const type = typeMatch?.[1] || 'application/octet-stream';
          const bytes = atob(base);
          const buf = new Uint8Array(bytes.length);
          for (let i = 0; i < bytes.length; i++) buf[i] = bytes.charCodeAt(i);
          const blob = new Blob([buf], { type });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          a.click();
          window.URL.revokeObjectURL(url);
          return;
        }
      }

      const blob = new Blob([content], { type: mime });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      message.error('No se pudo descargar el acuse de cancelación.');
    }
  };

  const handleDelete = () => {
    Modal.confirm({
      title: 'Eliminar borrador',
      content: '¿Estás seguro de que deseas eliminar este borrador de factura? Esta acción no se puede deshacer.',
      okText: 'Eliminar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await userService.deleteInvoice(id);
          message.success('Borrador eliminado correctamente');
          navigate(backPath);
        } catch (error) {
          console.error('Error deleting invoice:', error);
          message.error(error.response?.data?.message || 'Error al eliminar el borrador');
        }
      },
    });
  };

  if (loading) {
    return <Spin />;
  }

  if (!invoice) {
    return (
      <div>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(backPath)}>Regresar</Button>
        <div style={{ marginTop: 16 }}>Factura no encontrada.</div>
      </div>
    );
  }

  return (
    <>
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(backPath)}>Regresar</Button>
        <Title level={3} style={{ margin: 0 }}>
          Factura {invoice.folio || ''}-{invoice.serie || ''} {invoice.uuid ? `(${invoice.uuid})` : ''}
        </Title>
      </Space>

      {/* Resumen de pagos si es PPD */}
      {invoice?.metodo_pago === 'PPD' && (
        <Alert
          message="Resumen de Pagos"
          description={
            <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
              <div>
                <strong>Total Factura:</strong> ${invoiceTotal.toFixed(2)}
              </div>
              <div>
                <strong>Pagado:</strong> <span style={{ color: 'green' }}>${totalPaid.toFixed(2)}</span>
              </div>
              <div>
                <strong>Pendiente:</strong> <span style={{ color: isInvoiceFullyPaid ? 'green' : 'red' }}>
                  ${Math.max(invoiceTotal - totalPaid, 0).toFixed(2)}
                </span>
              </div>
            </div>
          }
          type={isInvoiceFullyPaid ? 'success' : 'info'}
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 16 }}>
        <Button onClick={handleDownloadXml} disabled={!invoice || invoice.status === 'borrador' || downloadingXml} loading={downloadingXml}>
          Descargar XML
        </Button>
        <Button type="primary" onClick={handleDownloadPdf} disabled={!invoice || downloadingPdf} loading={downloadingPdf}>
          Descargar PDF
        </Button>
        
        {invoice?.status === 'timbrada' && (
          <Button icon={<MailOutlined />} onClick={handleEmailClick}>
            Enviar por Correo
          </Button>
        )}

        {/* Componente externo de pagos: ocultar si ya está totalmente pagada */}
        {!isInvoiceFullyPaid && (
          <AddPaymentModal invoice={invoice} onCreated={refreshInvoice} />
        )}

        {invoice?.status === 'borrador' && (
          <Button danger onClick={handleDelete}>
            Eliminar Borrador
          </Button>
        )}

        {invoice?.status === 'cancelada' ? (
          <Button onClick={handleDownloadAcuse} disabled={!invoice}>
            Descargar acuse
          </Button>
        ) : (
          invoice?.status === 'timbrada' && (
            <Button danger onClick={openCancelModal}>
              Cancelar
            </Button>
          )
        )}
      </div>
      
      <Descriptions bordered column={{ xs: 1, sm: 2, md: 3 }} size="middle">
        <Descriptions.Item label="Estado">
          <Tag color={
            invoice?.status === 'timbrada'
              ? 'green'
              : invoice?.status === 'cancelada'
              ? 'red'
              : 'blue'
          }>
            {invoice?.status || '—'}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Folio fiscal (UUID)">{invoice.uuid || '—'}</Descriptions.Item>
        <Descriptions.Item label="Empresa">{invoice.company?.tax_name || '—'}</Descriptions.Item>
        <Descriptions.Item label="Cliente">{invoice.receiver?.tax_name || '—'}</Descriptions.Item>
        <Descriptions.Item label="RFC Cliente">{invoice.receiver?.tax_id || '—'}</Descriptions.Item>
        <Descriptions.Item label="Serie">{invoice.serie || '—'}</Descriptions.Item>
        <Descriptions.Item label="Folio">{invoice.folio || '—'}</Descriptions.Item>
        <Descriptions.Item label="Total">{invoice.total ? `$${parseFloat(invoice.total).toFixed(2)}` : '—'}</Descriptions.Item>
        <Descriptions.Item label="Fecha de creación">
          {invoice.created_at ? new Date(invoice.created_at).toLocaleString('es-MX') : '—'}
        </Descriptions.Item>
        {
          payments.length > 0 ? (
            <Descriptions.Item label="Pagos">
              <>
          <div style={{ height: '50vh', overflow: 'auto' }}>
            <Table
              columns={[
                {
                  title: 'Fecha de pago',
                  dataIndex: 'fecha_pago',
                  key: 'fecha_pago',
                  render: (val) => (val ? new Date(val).toLocaleDateString('es-MX') : '—'),
                },
                {
                  title: 'Monto',
                  dataIndex: 'monto',
                  key: 'monto',
                  render: (val, record) => `${record?.moneda || 'MXN'} $${Number(val || 0).toFixed(2)}`,
                },
                { title: 'Forma de pago', dataIndex: 'forma_pago', key: 'forma_pago' },
                {
                  title: 'Estado',
                  dataIndex: 'status',
                  key: 'status',
                  render: (s) => (
                    <Tag color={s === 'timbrada' ? 'green' : s === 'borrador' ? 'blue' : 'default'}>
                      {s || '—'}
                    </Tag>
                  ),
                },
                {
                  title: 'Serie',
                  dataIndex: 'serie',
                  key: 'serie',
                  render: (v) => v || '—',
                },
                {
                  title: 'Folio',
                  dataIndex: 'folio',
                  key: 'folio',
                  render: (v) => (v == null || v === '' ? '—' : String(v)),
                },
                { title: 'UUID', dataIndex: 'uuid', key: 'uuid', render: (v) => v || '—' },
                {
                  title: 'Documentos (DR)',
                  key: 'doc',
                  render: (_, record) => {
                    const items = (record.documents || []).filter((d) => d?.invoice?.id === invoice?.id);
                    if (!items.length) return '—';
                    return items.map((d, i) => (
                      <div key={i}>
                        {d.invoice?.serie || ''}-{d.invoice?.folio || ''} · Parcialidad {d.num_parcialidad} · Pagado $
                        {Number(d.importe_pagado || 0).toFixed(2)}
                      </div>
                    ));
                  },
                },
                {
            title: 'Acciones',
            key: 'actions',
            width: 140,
            render: (_, record) => (
              <Space size="small">
                {record.status === 'timbrada' && (
                  <>
                    <Tooltip title="Descargar XML">
                      <Button
                        shape="circle"
                        size="small"
                        icon={<FileTextOutlined />}
                        onClick={() => handleDownloadPaymentsXml({id:record.id})}
                      />
                    </Tooltip>
                    <Tooltip title="Descargar PDF">
                      <Button
                        shape="circle"
                        size="small"
                        type="primary"
                        icon={<FilePdfOutlined />}
                        onClick={() => handleDownloadPaymentsPdf({id:record.id})}
                      />
                    </Tooltip>
                    <Tooltip title="Cancelar">
                      <Button
                        shape="circle"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleCancelPayment(record.id)}
                        disabled={record.status === 'cancelada'}
                      />
                    </Tooltip>
                  </>
                )}
                
                {record.status !== 'timbrada' && (
                  <>
                    <Tooltip title="Editar">
                      <Button
                        shape="circle"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handleEditPayment(record)}
                      />
                    </Tooltip>
                    <Tooltip title="Timbrar">
                      <Button
                        shape="circle"
                        size="small"
                        type="primary"
                        icon={<CloudUploadOutlined />}
                        loading={stampingPayment[record.id]}
                        onClick={() => handleStampPayment(record.id)}
                      />
                    </Tooltip>
                    <Popconfirm
                      title="¿Eliminar pago?"
                      description="Esta acción no se puede deshacer."
                      onConfirm={() => handleDeletePayment(record.id)}
                      okText="Sí"
                      cancelText="No"
                    >
                      <Tooltip title="Eliminar">
                        <Button
                          shape="circle"
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                        />
                      </Tooltip>
                    </Popconfirm>
                  </>
                )}
              </Space>
            ),
          },
              ]}
              dataSource={payments}
              rowKey="id"
              loading={paymentsLoading}
              pagination={false}
              scroll={{ x: 'max-content' }}
            />
          </div>
        </>
            </Descriptions.Item>
          ) : null
        }
      </Descriptions>
    </div>

    <Modal
      title="Cancelar CFDI"
      open={cancelModalVisible}
      onCancel={() => setCancelModalVisible(false)}
      footer={null}
      destroyOnClose
    >
      <Form form={cancelForm} layout="vertical" onFinish={handleConfirmCancel}>
        <Form.Item
          label="Motivo de cancelación"
          name="motivo"
          rules={[{ required: true, message: 'Selecciona un motivo' }]}
        >
          <Select
            placeholder="Selecciona motivo (01–04)"
            onChange={setSelectedMotivo}
            loading={!motives.length}
          >
            {(motives.length ? motives : allowedCodes.map((code) => ({ code, description: code }))).map(
              (m) => (
                <Select.Option key={m.code} value={m.code}>
                  {m.code} - {m.description || m.code}
                </Select.Option>
              )
            )}
          </Select>
        </Form.Item>

        {(selectedMotivo === '01' || selectedMotivo === '04') && (
          <Form.Item
            label="UUID de sustitución"
            name="uuid_sustitucion"
            rules={[{ required: true, message: 'Requiere UUID de sustitución para motivo 01 o 04' }]}
          >
            <Input placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
          </Form.Item>
        )}

        <Form.Item>
          <Button type="primary" htmlType="submit" loading={cancelling}>
            Confirmar cancelación
          </Button>
        </Form.Item>
      </Form>
    </Modal>

    <Modal
      title="Enviar Factura por Correo"
      open={emailModalVisible}
      onCancel={() => setEmailModalVisible(false)}
      footer={null}
      destroyOnClose
    >
      <Form form={emailForm} layout="vertical" onFinish={handleSendEmail}>
        <Form.Item
          label="Correo destinatario"
          name="recipient_email"
          rules={[
            { required: true, message: 'Ingresa el correo' },
            { type: 'email', message: 'Correo inválido' },
          ]}
        >
          <Input placeholder="correo@dominio.com" />
        </Form.Item>

        <Form.Item
          label="Asunto"
          name="subject"
          rules={[{ required: true, message: 'El asunto es requerido' }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="Mensaje"
          name="body"
        >
          <Input.TextArea rows={4} />
        </Form.Item>

        <Form.Item>
          <Button type="primary" htmlType="submit" loading={sendingEmail} block>
            Enviar Correo
          </Button>
        </Form.Item>
      </Form>
    </Modal>
    </>
  );
};

export default InvoiceDetail;
